import { useEffect, useState } from 'react';

import { routes } from '../../../assets/constants';
import { ApiMethod } from '../../../common/models/api-types.type';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { toCanvasProduct } from '../content-studio/product-mapping';
import { ProductInfo } from './components/ShotCard';

/** Shared across the page: a product is fetched once however many shots use it. */
const cache = new Map<string, ProductInfo>();
const inflight = new Map<string, Promise<ProductInfo | null>>();

function nameOf(raw: any): string {
    const n = raw?.name;
    if (!n) return '';
    if (typeof n === 'string') return n;
    return n.en || Object.values(n)[0] || '';
}

export function rememberProducts(items: ProductInfo[]) {
    for (const p of items) if (p?.uuid) cache.set(p.uuid, { ...cache.get(p.uuid), ...p });
}

/**
 * Names and pictures for the products on the board — Ardi adds products by
 * uuid, and a card has to show which one it is.
 */
export function useProductInfo(uuids: string[]): Map<string, ProductInfo> {
    const { sendAuthGuardedRequest } = useAuthContext() as any;
    const [, bump] = useState(0);
    const key = [...new Set(uuids)].sort().join(',');

    useEffect(() => {
        const missing = key.split(',').filter((u) => u && !cache.has(u));
        if (!missing.length) return undefined;
        let alive = true;
        Promise.all(missing.map((uuid) => {
            if (!inflight.has(uuid)) {
                inflight.set(uuid, (async () => {
                    try {
                        const res: any = await sendAuthGuardedRequest(ApiMethod.GET, routes.products.findOne(uuid));
                        const raw = res?.data || res;
                        const p = toCanvasProduct(raw);
                        const info: ProductInfo = {
                            uuid,
                            name: nameOf(raw) || String(p.name || ''),
                            thumbnailUrl: (p as any).thumbnailPath || (p as any).originalImagePath || (p as any).surfaceTexturePath,
                            imageUrl: (p as any).originalImagePath || (p as any).thumbnailPath || (p as any).surfaceTexturePath,
                            visualizationType: p.visualizationType,
                            category: (p as any).category || raw?.organization?.productType,
                            modelPath: (p as any).modelPath,
                        };
                        cache.set(uuid, info);
                        return info;
                    } catch {
                        return null;
                    } finally {
                        inflight.delete(uuid);
                    }
                })());
            }
            return inflight.get(uuid);
        })).then(() => alive && bump((n) => n + 1));
        return () => { alive = false; };
    }, [key, sendAuthGuardedRequest]);

    return new Map(key.split(',').filter((u) => cache.has(u)).map((u) => [u, cache.get(u)!]));
}
